import { useEffect, useMemo, useState } from 'react';
import { createFileRoute, Link } from '@tanstack/react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import {
  listLockPolicies,
  upsertLockPolicy,
  syncAllLockPolicies,
  getProviderConfig,
} from '@/features/lock-engine/api';
import {
  POLICY_LEVELS,
  RESTRICTION_TOKENS,
  type EscalationLevel,
  type RestrictionToken,
  type TenantLockPolicy,
  type TenantLockPolicyUpsert,
  type ProviderConfigResponse,
} from '@/features/lock-engine/types';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { Input } from '@/components/ui/input';
import { PhoneInput } from '@/components/ui/phone-input';
import { parsePhone } from '@/lib/phone';
import { Textarea } from '@/components/ui/textarea';
import { Checkbox } from '@/components/ui/checkbox';
import { Label } from '@/components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  AlertTriangle,
  ArrowLeft,
  CheckCircle,
  Info,
  Lock,
  Loader2,
  MessageSquare,
  Pencil,
  Phone,
  Plus,
  RefreshCw,
  ShieldAlert,
  Smartphone,
  X,
  XCircle,
} from '@/lib/icons';
import { useToast } from '@/hooks/use-toast';
import { cn } from '@/lib/utils';
import { formatDateTime } from '@/lib/dates';

export const Route = createFileRoute('/_layout/lock-engine/policies')({
  component: LockPoliciesPage,
});

// ── Level metadata ───────────────────────────────────────────

const LEVEL_META: Record<
  EscalationLevel,
  { label: string; description: string; icon: React.ReactNode; cls: string }
> = {
  none: {
    label: 'None',
    description: '',
    icon: <></>,
    cls: '',
  },
  warning: {
    label: 'Warning',
    description: 'Customer is in arrears — show a soft reminder.',
    icon: <AlertTriangle className="w-4 h-4" />,
    cls: 'bg-amber-500/15 text-amber-600 border-amber-500/30 dark:text-amber-400',
  },
  partial_lock: {
    label: 'Partial Lock',
    description:
      'Restrict outgoing communication; the device stays partially usable.',
    icon: <ShieldAlert className="w-4 h-4" />,
    cls: 'bg-orange-500/15 text-orange-600 border-orange-500/30 dark:text-orange-400',
  },
  full_lock: {
    label: 'Full Lock',
    description: 'Lock the screen and block calls/SMS until paid.',
    icon: <Lock className="w-4 h-4" />,
    cls: 'bg-red-500/15 text-red-600 border-red-500/30 dark:text-red-400',
  },
  defaulted: {
    label: 'Defaulted',
    description:
      'Treat the contract as defaulted — full lock + SIM lock for recovery.',
    icon: <XCircle className="w-4 h-4" />,
    cls: 'bg-rose-500/15 text-rose-600 border-rose-500/30 dark:text-rose-400',
  },
};

// Sensible defaults for first-time configuration. Mirrors the e2e doc.
const LEVEL_DEFAULTS: Record<EscalationLevel, TenantLockPolicyUpsert> = {
  none: {
    message: null,
    title: null,
    restrictions: [],
    allowedNumbers: [],
    allowedApps: [],
  },
  warning: {
    message: 'Lacagta lagu daahay. Bixi inta uu xilligu jiro.',
    title: 'Lacag Bixin',
    restrictions: ['WATERMARK', 'AUTO_POPUP'],
    allowedNumbers: [],
    allowedApps: ['com.introinnovation.nuurmobile', 'com.safarifone.waafi'],
  },
  partial_lock: {
    message: 'Lacagta lagu daahay. Bixi inta uu xilligu jiro.',
    title: 'Lacag Bixin',
    restrictions: ['WATERMARK', 'AUTO_POPUP', 'BLOCK_OUTGOING_CALLS'],
    allowedNumbers: ['999'],
    allowedApps: ['com.introinnovation.nuurmobile', 'com.safarifone.waafi'],
  },
  full_lock: {
    message: 'Lacagta lagu daahay. Bixi inta uu xilligu jiro.',
    title: 'Lacag Bixin',
    // No BLOCK_ALL_APPS: PT has no allow-list, so it would strand allowedApps.
    restrictions: ['WATERMARK', 'SCREEN_LOCK', 'BLOCK_OUTGOING_CALLS', 'BLOCK_SMS'],
    allowedNumbers: ['999'],
    allowedApps: ['com.introinnovation.nuurmobile', 'com.safarifone.waafi'],
  },
  defaulted: {
    message: 'Lacagta lagu daahay. Bixi inta uu xilligu jiro.',
    title: 'Lacag Bixin',
    restrictions: [
      'WATERMARK',
      'SCREEN_LOCK',
      'SIM_LOCK',
      'BLOCK_OUTGOING_CALLS',
      'BLOCK_SMS',
    ],
    allowedNumbers: ['999'],
    allowedApps: ['com.introinnovation.nuurmobile', 'com.safarifone.waafi'],
  },
};

// ── Page ─────────────────────────────────────────────────────

function LockPoliciesPage() {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  const policiesQuery = useQuery({
    queryKey: ['lock-engine', 'policies'],
    queryFn: listLockPolicies,
  });

  const providerConfigQuery = useQuery({
    queryKey: ['lock-engine', 'provider-config'],
    queryFn: getProviderConfig,
    staleTime: 60_000,
  });

  const [editing, setEditing] = useState<EscalationLevel | null>(null);

  const syncAllMutation = useMutation({
    mutationFn: syncAllLockPolicies,
    onSuccess: (result) => {
      if (result.succeeded) {
        // `succeeded` is only the envelope; per-level outcomes live in `results`.
        const failures = (result.value?.results ?? []).flatMap((r) =>
          r.syncResults
            .filter((s) => s.status === 'failed')
            .map((s) => `${r.level}: ${s.detail ?? 'sync failed'}`)
        );
        if (failures.length > 0) {
          toast({
            title: `Sync failed for ${failures.length} level(s)`,
            description: failures[0],
            variant: 'destructive',
          });
        } else {
          toast({ title: 'Sync complete', description: 'All levels pushed to providers.' });
        }
        queryClient.invalidateQueries({ queryKey: ['lock-engine', 'policies'] });
        queryClient.invalidateQueries({ queryKey: ['lock-engine', 'provider-config'] });
      } else {
        toast({ title: 'Sync failed', description: result.errors?.[0] ?? 'Unknown error', variant: 'destructive' });
      }
    },
    onError: () => {
      toast({ title: 'Sync failed', description: 'Unable to reach server.', variant: 'destructive' });
    },
  });

  const policiesByLevel = useMemo(() => {
    const map = new Map<EscalationLevel, TenantLockPolicy>();
    for (const p of policiesQuery.data?.value ?? []) {
      map.set(p.level, p);
    }
    return map;
  }, [policiesQuery.data]);

  const providerConfig = providerConfigQuery.data?.value as ProviderConfigResponse | undefined;

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <Link
            to="/lock-engine"
            className="inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground transition-colors"
          >
            <ArrowLeft className="w-3 h-3" /> Lock Engine
          </Link>
          <h1 className="text-2xl font-bold tracking-tight mt-1">
            Lock Policies
          </h1>
          <p className="text-muted-foreground text-sm mt-1">
            Per-escalation-level rules applied to overdue devices. Each policy
            is a canonical, provider-agnostic shape — adapters (PayTrigger,
            Knox, Software) translate it on push.
          </p>
        </div>
        <Button
          variant="outline"
          size="sm"
          onClick={() => syncAllMutation.mutate()}
          disabled={syncAllMutation.isPending || policiesQuery.isLoading}
        >
          {syncAllMutation.isPending ? (
            <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" />
          ) : (
            <RefreshCw className="w-3.5 h-3.5 mr-1.5" />
          )}
          Sync all to provider
        </Button>
      </div>

      <div className="grid gap-4 lg:grid-cols-2">
        {POLICY_LEVELS.map((level) => {
          const meta = LEVEL_META[level];
          const policy = policiesByLevel.get(level);
          const configured = !!policy;
          return (
            <Card
              key={level}
              className="border-0 shadow-md flex flex-col"
            >
              <CardHeader className="pb-3">
                <div className="flex items-start justify-between gap-3">
                  <div className="space-y-1">
                    <CardTitle className="text-base flex items-center gap-2">
                      <Badge className={cn('gap-1', meta.cls)}>
                        {meta.icon} {meta.label}
                      </Badge>
                      {!configured && (
                        <span className="text-xs text-muted-foreground font-normal">
                          (not configured)
                        </span>
                      )}
                      {configured && <SyncBadge policy={policy!} />}
                    </CardTitle>
                    <CardDescription className="text-xs">
                      {meta.description}
                    </CardDescription>
                  </div>
                  <Button
                    size="sm"
                    variant={configured ? 'outline' : 'default'}
                    onClick={() => setEditing(level)}
                    disabled={policiesQuery.isLoading}
                  >
                    <Pencil className="w-3.5 h-3.5 mr-1.5" />
                    {configured ? 'Edit' : 'Configure'}
                  </Button>
                </div>
              </CardHeader>
              <CardContent className="flex-1">
                {policiesQuery.isLoading ? (
                  <Skeleton className="h-32 w-full" />
                ) : (
                  <PolicySummary policy={policy} />
                )}
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* Provider configuration panel */}
      <ProviderConfigPanel config={providerConfig} isLoading={providerConfigQuery.isLoading} />

      {editing && (
        <PolicyEditDialog
          level={editing}
          existing={policiesByLevel.get(editing) ?? null}
          onClose={() => setEditing(null)}
        />
      )}
    </div>
  );
}

// ── Read-only summary ────────────────────────────────────────

function PolicySummary({ policy }: { policy?: TenantLockPolicy }) {
  if (!policy) {
    return (
      <p className="text-xs text-muted-foreground italic">
        No policy configured. Adapters fall back to their default behaviour for
        this level.
      </p>
    );
  }

  const restrictions = policy.restrictions ?? [];
  const allowedNumbers = policy.allowedNumbers ?? [];
  const allowedApps = policy.allowedApps ?? [];

  return (
    <div className="space-y-3 text-sm">
      <div>
        <p className="text-xs font-medium text-muted-foreground uppercase tracking-wider mb-1.5 flex items-center gap-1">
          <MessageSquare className="w-3 h-3" /> Message
        </p>
        <p className="text-sm">
          {policy.message ? (
            policy.message
          ) : (
            <span className="text-muted-foreground italic">none</span>
          )}
        </p>
      </div>
      <div>
        <p className="text-xs font-medium text-muted-foreground uppercase tracking-wider mb-1.5">
          Restrictions
        </p>
        {restrictions.length === 0 ? (
          <span className="text-muted-foreground italic text-xs">none</span>
        ) : (
          <div className="flex flex-wrap gap-1.5">
            {restrictions.map((r) => (
              <Badge key={r} variant="outline" className="font-mono text-[10px]">
                {r}
              </Badge>
            ))}
          </div>
        )}
      </div>
      <div className="grid grid-cols-2 gap-3">
        <div>
          <p className="text-xs font-medium text-muted-foreground uppercase tracking-wider mb-1.5 flex items-center gap-1">
            <Phone className="w-3 h-3" /> Allowed Numbers
          </p>
          {allowedNumbers.length === 0 ? (
            <span className="text-muted-foreground italic text-xs">none</span>
          ) : (
            <div className="flex flex-wrap gap-1.5">
              {allowedNumbers.map((n) => (
                <Badge
                  key={n}
                  variant="outline"
                  className="font-mono text-[10px]"
                >
                  {n}
                </Badge>
              ))}
            </div>
          )}
        </div>
        <div>
          <p className="text-xs font-medium text-muted-foreground uppercase tracking-wider mb-1.5 flex items-center gap-1">
            <Smartphone className="w-3 h-3" /> Allowed Apps
          </p>
          {allowedApps.length === 0 ? (
            <span className="text-muted-foreground italic text-xs">none</span>
          ) : (
            <div className="flex flex-wrap gap-1.5">
              {allowedApps.map((a) => (
                <Badge
                  key={a}
                  variant="outline"
                  className="font-mono text-[10px]"
                >
                  {a}
                </Badge>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ── Edit dialog ──────────────────────────────────────────────

function PolicyEditDialog({
  level,
  existing,
  onClose,
}: {
  level: EscalationLevel;
  existing: TenantLockPolicy | null;
  onClose: () => void;
}) {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const meta = LEVEL_META[level];

  const initial: TenantLockPolicyUpsert = useMemo(
    () =>
      existing
        ? {
            message: existing.message ?? '',
            title: existing.title ?? '',
            restrictions: existing.restrictions ?? [],
            allowedNumbers: existing.allowedNumbers ?? [],
            allowedApps: existing.allowedApps ?? [],
          }
        : LEVEL_DEFAULTS[level],
    [existing, level]
  );

  const [form, setForm] = useState<TenantLockPolicyUpsert>(initial);

  // Reset form when the level/existing changes (dialog reopened on a
  // different level without unmount).
  useEffect(() => {
    setForm(initial);
  }, [initial]);

  const upsert = useMutation({
    mutationFn: () =>
      upsertLockPolicy(level, {
        ...form,
        message: form.message?.trim() ? form.message.trim() : null,
        title: form.title?.trim() ? form.title.trim() : null,
      }),
    onSuccess: (result) => {
      if (result.succeeded) {
        const syncResults = result.value?.syncResults ?? [];
        const failed = syncResults.filter((s) => s.status === 'failed');
        if (failed.length > 0) {
          toast({
            title: 'Policy saved, but not synced to provider',
            description: failed[0].detail ?? `${failed.length} provider(s) failed to sync.`,
            variant: 'destructive',
          });
        } else {
          toast({
            title: 'Policy saved',
            description: `${meta.label} policy applied and synced.`,
          });
        }
        queryClient.invalidateQueries({
          queryKey: ['lock-engine', 'policies'],
        });
        onClose();
      } else {
        toast({
          title: 'Save failed',
          description: result.errors?.[0] ?? 'Unable to save policy',
          variant: 'destructive',
        });
      }
    },
    onError: () => {
      toast({
        title: 'Save failed',
        description: 'Unable to save policy',
        variant: 'destructive',
      });
    },
  });

  // PT documents title as optional but rejects the payload with 514 without it.
  const titleRequired = form.restrictions.some(
    (r) => r === 'AUTO_POPUP' || r === 'SCREEN_LOCK'
  );
  const titleMissing = titleRequired && !form.title?.trim();

  const toggleRestriction = (token: RestrictionToken, checked: boolean) => {
    setForm((prev) => ({
      ...prev,
      restrictions: checked
        ? [...new Set([...prev.restrictions, token])]
        : prev.restrictions.filter((r) => r !== token),
    }));
  };

  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Badge className={cn('gap-1', meta.cls)}>
              {meta.icon} {meta.label}
            </Badge>
            Lock Policy
          </DialogTitle>
          <DialogDescription>{meta.description}</DialogDescription>
        </DialogHeader>

        <div className="space-y-5 py-2">
          <div className="space-y-1.5">
            <Label htmlFor="policy-message" className="text-xs">
              Message shown to the customer
            </Label>
            <Textarea
              id="policy-message"
              rows={3}
              placeholder="e.g. Lacagta lagu daahay. Bixi inta uu xilligu jiro."
              value={form.message ?? ''}
              onChange={(e) =>
                setForm((prev) => ({ ...prev, message: e.target.value }))
              }
            />
            <p className="text-[11px] text-muted-foreground">
              Rendered as the lock-screen overlay text. Typically Somali.
            </p>
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="policy-title" className="text-xs">
              Title / heading{' '}
              {titleRequired ? (
                <span className="text-destructive">*</span>
              ) : (
                '(optional)'
              )}
            </Label>
            <Input
              id="policy-title"
              placeholder="e.g. Lacag Bixin"
              value={form.title ?? ''}
              aria-invalid={titleMissing}
              onChange={(e) =>
                setForm((prev) => ({ ...prev, title: e.target.value }))
              }
            />
            <p className="text-[11px] text-muted-foreground">
              Heading for screen-lock / popup overlay.
            </p>
          </div>

          <div className="space-y-2">
            <Label className="text-xs">Restrictions</Label>
            <div className="grid sm:grid-cols-2 gap-2">
              {RESTRICTION_TOKENS.map((r) => {
                const checked = form.restrictions.includes(r.token);
                return (
                  <label
                    key={r.token}
                    className={cn(
                      'flex items-start gap-2.5 rounded-lg border p-2.5 cursor-pointer transition-colors',
                      checked
                        ? 'border-primary/40 bg-primary/5'
                        : 'border-border hover:bg-muted/40'
                    )}
                  >
                    <Checkbox
                      checked={checked}
                      onCheckedChange={(v) =>
                        toggleRestriction(r.token, v === true)
                      }
                      className="mt-0.5"
                    />
                    <div className="space-y-0.5">
                      <p className="text-sm font-medium leading-tight">
                        {r.label}
                      </p>
                      <p className="text-[11px] text-muted-foreground leading-snug">
                        {r.description}
                      </p>
                      <p className="text-[10px] font-mono text-muted-foreground/70">
                        {r.token}
                      </p>
                    </div>
                  </label>
                );
              })}
            </div>
          </div>

          <ChipList
            phone
            label="Allowed numbers"
            description="MSISDNs always reachable while locked (branch hotline, emergency)."
            placeholder="+252632345003"
            icon={<Phone className="w-3 h-3" />}
            values={form.allowedNumbers}
            onChange={(values) =>
              setForm((prev) => ({ ...prev, allowedNumbers: values }))
            }
          />

          <ChipList
            label="Allowed apps"
            description="Package names always launchable while locked (Nuur app, money app). The provider's app whitelist is managed in the Transsion console."
            placeholder="com.introinnovation.nuurmobile"
            icon={<Smartphone className="w-3 h-3" />}
            values={form.allowedApps}
            onChange={(values) =>
              setForm((prev) => ({ ...prev, allowedApps: values }))
            }
          />
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={upsert.isPending}>
            Cancel
          </Button>
          <Button
            onClick={() => upsert.mutate()}
            disabled={upsert.isPending || titleMissing}
          >
            {upsert.isPending ? 'Saving…' : 'Save policy'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ── Chip list (allowed-numbers / allowed-apps) ──────────────

function ChipList({
  label,
  description,
  placeholder,
  icon,
  values,
  onChange,
  /**
   * Entries are phone numbers: use the phone field, and canonicalise each one
   * before it becomes a chip. The allow-list is matched on-device against the
   * dialled number, so `0612345678` and `+252612345678` must not both be
   * addable — one of them would silently never match.
   */
  phone = false,
}: Readonly<{
  label: string;
  description: string;
  placeholder: string;
  icon: React.ReactNode;
  values: string[];
  onChange: (next: string[]) => void;
  phone?: boolean;
}>) {
  const [draft, setDraft] = useState('');

  const parsedDraft = phone ? parsePhone(draft) : null;
  const canAdd = phone ? parsedDraft!.isValid : !!draft.trim();

  const add = () => {
    const entry = phone ? (parsedDraft!.e164 ?? '') : draft.trim();
    if (!entry || values.includes(entry)) {
      setDraft('');
      return;
    }
    onChange([...values, entry]);
    setDraft('');
  };

  const remove = (value: string) => {
    onChange(values.filter((v) => v !== value));
  };

  return (
    <div className="space-y-1.5">
      <Label className="text-xs flex items-center gap-1">
        {icon} {label}
      </Label>
      <div className="flex gap-2">
        {phone ? (
          <PhoneInput value={draft} onChange={setDraft} onEnter={add} />
        ) : (
          <Input
            placeholder={placeholder}
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                add();
              }
            }}
          />
        )}
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={add}
          disabled={!canAdd}
        >
          <Plus className="w-3.5 h-3.5 mr-1" /> Add
        </Button>
      </div>
      {values.length > 0 && (
        <div className="flex flex-wrap gap-1.5 pt-1">
          {values.map((v) => (
            <Badge
              key={v}
              variant="outline"
              className="font-mono text-[10px] gap-1 pl-2 pr-1 py-0.5"
            >
              {v}
              <button
                type="button"
                onClick={() => remove(v)}
                className="hover:bg-muted rounded p-0.5 transition-colors"
                aria-label={`Remove ${v}`}
              >
                <X className="w-3 h-3" />
              </button>
            </Badge>
          ))}
        </div>
      )}
      <p className="text-[11px] text-muted-foreground">{description}</p>
    </div>
  );
}

// ── Sync status badge ───────────────────────────────────────

function SyncBadge({ policy }: { policy: TenantLockPolicy }) {
  if (!policy.lastSyncStatus) return null;

  const statusMap: Record<string, { cls: string; icon: React.ReactNode; label: string }> = {
    synced: {
      cls: 'bg-emerald-500/15 text-emerald-600 border-emerald-500/30 dark:text-emerald-400',
      icon: <CheckCircle className="w-3 h-3" />,
      label: 'Synced',
    },
    skipped: {
      cls: 'bg-slate-500/15 text-slate-500 border-slate-500/30',
      icon: <Info className="w-3 h-3" />,
      label: 'Skipped',
    },
    failed: {
      cls: 'bg-red-500/15 text-red-600 border-red-500/30 dark:text-red-400',
      icon: <XCircle className="w-3 h-3" />,
      label: 'Sync failed',
    },
  };

  const s = statusMap[policy.lastSyncStatus] ?? statusMap.failed;
  const ago = policy.lastSyncedAt
    ? formatDateTime(policy.lastSyncedAt)
    : '';

  return (
    <Badge
      variant="outline"
      className={cn('gap-1 text-[10px] font-normal', s.cls)}
      title={
        policy.lastSyncError
          ? `Error: ${policy.lastSyncError}\n${ago}`
          : ago
      }
    >
      {s.icon} {s.label}
    </Badge>
  );
}

// ── Provider configuration panel ────────────────────────────

function ProviderConfigPanel({
  config,
  isLoading,
}: {
  config?: ProviderConfigResponse;
  isLoading: boolean;
}) {
  if (isLoading) {
    return (
      <Card className="border-0 shadow-md">
        <CardHeader className="pb-3">
          <CardTitle className="text-base">Provider Configuration</CardTitle>
        </CardHeader>
        <CardContent>
          <Skeleton className="h-24 w-full" />
        </CardContent>
      </Card>
    );
  }

  if (!config || config.error) {
    return (
      <Card className="border-0 shadow-md">
        <CardHeader className="pb-3">
          <CardTitle className="text-base">Provider Configuration</CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-xs text-muted-foreground italic">
            {config?.error ?? 'Unable to load provider configuration.'}
          </p>
        </CardContent>
      </Card>
    );
  }

  const hasDrift = config.missingFromProvider.length > 0 ||
    config.appWhitelist.some((a) => !a.declared);

  return (
    <Card className="border-0 shadow-md">
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <div>
            <CardTitle className="text-base flex items-center gap-2">
              Provider Configuration
              {hasDrift && (
                <Badge variant="outline" className="bg-amber-500/15 text-amber-600 border-amber-500/30 dark:text-amber-400 text-[10px] gap-1">
                  <AlertTriangle className="w-3 h-3" /> Drift detected
                </Badge>
              )}
            </CardTitle>
            <CardDescription className="text-xs mt-1">
              Read-back of PayTrigger's actual configuration (§8). The app
              whitelist is console-managed — mismatches indicate drift.
            </CardDescription>
          </div>
        </div>
      </CardHeader>
      <CardContent>
        <div className="space-y-4">
          {/* App whitelist */}
          <div>
            <p className="text-xs font-medium text-muted-foreground uppercase tracking-wider mb-1.5 flex items-center gap-1">
              <Smartphone className="w-3 h-3" /> App Whitelist (console-managed)
            </p>
            {config.appWhitelist.length === 0 ? (
              <span className="text-muted-foreground italic text-xs">empty</span>
            ) : (
              <div className="flex flex-wrap gap-1.5">
                {config.appWhitelist.map((app) => (
                  <Badge
                    key={app.packageName}
                    variant="outline"
                    className={cn(
                      'font-mono text-[10px]',
                      app.declared
                        ? 'border-emerald-500/40 bg-emerald-500/10'
                        : 'border-amber-500/40 bg-amber-500/10'
                    )}
                    title={app.declared ? 'Declared in portal' : 'Not declared in portal — possibly stale'}
                  >
                    {app.declared ? (
                      <CheckCircle className="w-3 h-3 mr-1 text-emerald-500" />
                    ) : (
                      <AlertTriangle className="w-3 h-3 mr-1 text-amber-500" />
                    )}
                    {app.packageName}
                  </Badge>
                ))}
              </div>
            )}
            {config.missingFromProvider.length > 0 && (
              <div className="mt-2">
                <p className="text-[11px] text-amber-600 dark:text-amber-400 mb-1">
                  Missing from provider (declared in portal but not in console):
                </p>
                <div className="flex flex-wrap gap-1.5">
                  {config.missingFromProvider.map((pkg) => (
                    <Badge key={pkg} variant="outline" className="font-mono text-[10px] border-red-500/40 bg-red-500/10">
                      {pkg}
                    </Badge>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Phone number whitelists */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <p className="text-xs font-medium text-muted-foreground uppercase tracking-wider mb-1.5 flex items-center gap-1">
                <Phone className="w-3 h-3" /> Incoming whitelist
              </p>
              {config.callInPhoneNum.length === 0 ? (
                <span className="text-muted-foreground italic text-xs">empty</span>
              ) : (
                <div className="flex flex-wrap gap-1.5">
                  {config.callInPhoneNum.map((n) => (
                    <Badge key={n} variant="outline" className="font-mono text-[10px]">{n}</Badge>
                  ))}
                </div>
              )}
            </div>
            <div>
              <p className="text-xs font-medium text-muted-foreground uppercase tracking-wider mb-1.5 flex items-center gap-1">
                <Phone className="w-3 h-3" /> Outgoing whitelist
              </p>
              {config.callOutPhoneNum.length === 0 ? (
                <span className="text-muted-foreground italic text-xs">empty</span>
              ) : (
                <div className="flex flex-wrap gap-1.5">
                  {config.callOutPhoneNum.map((n) => (
                    <Badge key={n} variant="outline" className="font-mono text-[10px]">{n}</Badge>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
